import { useId } from 'react'
import { Settings } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { IntuiSlider } from '@/components/intueri/IntuiSlider'

import {
  LINE_WIDTH_MAX,
  LINE_WIDTH_MIN,
  LINE_WIDTH_STEP,
  SPACING_MAX,
  SPACING_MIN,
  SPACING_STEP,
  type DisplaySettings,
} from './displaySettings'

/** The gear and its popover; see `displaySettings.ts` for what each control
 *  means and why the defaults are what they are. Presentational: state lives
 *  with the page so the canvas and the settings read one source. */
interface Props {
  settings: DisplaySettings
  onChange: (next: DisplaySettings) => void
}

interface SliderProps {
  label: string
  value: number
  /** The value as printed beside the label. */
  shown: string
  testId: string
  min: number
  max: number
  step: number
  onChange: (value: number) => void
}

/** A labelled slider row: the label and its value on one line, the slider under it. */
const DisplaySlider = ({ label, value, shown, testId, min, max, step, onChange }: SliderProps) => {
  // The visible label names the slider, for a screen reader too.
  const labelId = useId()
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between text-sm font-medium text-foreground">
        <span id={labelId}>{label}</span>
        <span className="tabular-nums" data-testid={`${testId}-value`}>
          {shown}
        </span>
      </div>
      <IntuiSlider
        data-testid={testId}
        thumbLabelledBy={labelId}
        // The design's slider (Display frame, DSGN-63): a thin 3px track, light
        // grey with a hairline outline after the thumb and black up to it, and a
        // 14px white thumb ringed in the switches' teal. The outline is an inset
        // shadow rather than a border so the black range paints over it.
        className="[&>span:first-child]:h-[3px] [&>span:first-child]:bg-[#e5e5e5] [&>span:first-child]:shadow-[inset_0_0_0_1px_#c4c4c4] [&>span:first-child>span]:bg-neutral-900 [&_[role=slider]]:size-3.5 [&_[role=slider]]:border-2 [&_[role=slider]]:border-[#34728d] [&_[role=slider]]:bg-white [&_[role=slider]]:shadow-[0_1px_2px_rgba(0,0,0,0.2)] dark:[&>span:first-child]:bg-neutral-700 dark:[&>span:first-child]:shadow-[inset_0_0_0_1px_#525252] dark:[&>span:first-child>span]:bg-neutral-100 dark:[&_[role=slider]]:bg-neutral-900"
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={([v]) => onChange(v)}
      />
    </div>
  )
}

const DisplaySettingsPopover = ({ settings, onChange }: Props) => {
  const { t } = useTranslation()
  const set = <K extends keyof DisplaySettings>(key: K, value: DisplaySettings[K]) =>
    onChange({ ...settings, [key]: value })

  const switches: Array<{ key: 'entityLabels' | 'edgeLabels' | 'orphans'; label: string }> = [
    { key: 'entityLabels', label: t('knowledge_graph:toolbar.entity_label', { defaultValue: 'Entity Label' }) },
    { key: 'edgeLabels', label: t('knowledge_graph:toolbar.edge_label', { defaultValue: 'Edge Label' }) },
    { key: 'orphans', label: t('knowledge_graph:toolbar.orphans', { defaultValue: 'Orphans' }) },
  ]
  const thicknessLabel = t('knowledge_graph:toolbar.line_thickness', { defaultValue: 'Line Thickness' })
  const spacingLabel = t('knowledge_graph:toolbar.spacing', { defaultValue: 'Spacing' })

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          // Matches the segmented control's height so the two sit level.
          className="h-9 w-9 rounded-md border-neutral-200 bg-background shadow-none dark:border-neutral-700"
          aria-label={String(t('knowledge_graph:toolbar.settings', { defaultValue: 'Display settings' }))}
        >
          <Settings className="size-4" strokeWidth={1.75} />
        </Button>
      </PopoverTrigger>
      {/* The design's Display popover at its true size (the Figma export is
          2x): 280 x 255px, 16px padding (30px under the last slider), 14px labels on a 28px pitch with
          teal switches, the thickness value as a bare number, thin 3px slider
          tracks with a 14px white, teal-ringed thumb. Its top sits 4px ABOVE the toolbar's
          bottom border — the card overlaps the line, where the Filter menu
          hangs from it. */}
      <PopoverContent align="end" sideOffset={8} className="w-[280px] rounded-xl px-4 pt-4 pb-[30px]">
        <div className="flex flex-col gap-2">
          <span className="mb-1 text-sm font-medium text-neutral-500 dark:text-neutral-400">
            {t('knowledge_graph:toolbar.display', { defaultValue: 'Display' })}
          </span>
          {switches.map(({ key, label }) => (
            <label
              key={key}
              className="flex items-center justify-between gap-3 text-sm font-medium text-foreground cursor-pointer select-none"
            >
              {label}
              <Switch
                aria-label={label}
                checked={settings[key]}
                onCheckedChange={(v) => set(key, v)}
                className="data-[state=checked]:bg-[#34728d] data-[state=unchecked]:bg-neutral-200 [&>span]:shadow-none"
              />
            </label>
          ))}
          {/* The design's slider rows: the label 21px above its slider, 35px
              from one slider to the next label (measured on the reference app). */}
          <div className="flex flex-col gap-[22px] pt-1">
            <DisplaySlider
              label={spacingLabel}
              value={settings.spacing}
              shown={`${settings.spacing}%`}
              testId="kg-spacing"
              min={SPACING_MIN}
              max={SPACING_MAX}
              step={SPACING_STEP}
              onChange={(v) => set('spacing', v)}
            />
            <DisplaySlider
              label={thicknessLabel}
              value={settings.lineWidth}
              shown={settings.lineWidth.toFixed(1)}
              testId="kg-line-thickness"
              min={LINE_WIDTH_MIN}
              max={LINE_WIDTH_MAX}
              step={LINE_WIDTH_STEP}
              onChange={(v) => set('lineWidth', v)}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default DisplaySettingsPopover
